// FormGroups.tsx
import React, { useState, useEffect } from "react";
import "../../../index.css";
import {
  Box,
  Checkbox,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import CheckedIcon from "../../../assets/CheckBox.svg";
import useColors from "../../../theme/useColors";
import { OTHER_OPTION_VALUE } from "../../../constants/app-constant";

// Common interface for both checkbox and radio groups
interface FormGroupBaseProps {
  title: string;
  required?: boolean;
  className?: string;
  isPreviewMode?: boolean; // Add preview mode prop
}

// CheckboxGroup Component
interface CheckboxGroupProps extends FormGroupBaseProps {
  options: string[];
  otherOption?: boolean;
  /** Option labels that show a specify-below text field when selected (uses each label as-is). */
  specifyBelowForOptions?: string[];
  /** Label for the legacy separate "Other" row; defaults to OTHER_OPTION_VALUE. */
  otherOptionLabel?: string;
  onChange?: (selectedOptions: string[]) => void;
  onOtherChange?: (text: string) => void;
  selectedOptions?: string[];
  otherText?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  title,
  options,
  required = false,
  otherOption = false,
  specifyBelowForOptions = [],
  otherOptionLabel = OTHER_OPTION_VALUE,
  onChange,
  onOtherChange,
  selectedOptions = [],
  otherText = "",
  className,
  isPreviewMode = false, // Default to false
}) => {
  // Use state from props if provided, otherwise use local state
  const [selected, setSelected] = useState<string[]>(selectedOptions);
  const [localOtherText, setLocalOtherText] = useState<string>(otherText);

  // Update local state when props change
  useEffect(() => {
    setSelected(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    setLocalOtherText(otherText);
  }, [otherText]);

  const handleChange = (option: string, checked: boolean) => {
    let newSelected: string[];

    if (checked) {
      if (
        option?.toLowerCase().trim() === "unable to determine" ||
        selected?.includes("Unable to determine")
      ) {
        newSelected = [option];
        handleOtherTextChange("");
      } else newSelected = [...selected, option];
    } else {
      newSelected = selected.filter((item) => item !== option);
      if (
        specifyBelowForOptions.includes(option) ||
        option === otherOptionLabel
      ) {
        handleOtherTextChange("");
      }
    }

    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const handleOtherTextChange = (value: string) => {
    setLocalOtherText(value);
    onOtherChange?.(value);
  };

  const colors = useColors();

  const showSpecifyInput = (option: string) =>
    specifyBelowForOptions.includes(option) && selected.includes(option);

  const hasVisibleSpecifyInput = options.some((option) =>
    showSpecifyInput(option)
  );

  const renderSpecifyInput = () => (
    <TextInput
      maxLength={100}
      placeholder="Please specify"
      value={localOtherText}
      readOnly={isPreviewMode}
      onChange={(event) => handleOtherTextChange(event.currentTarget.value)}
      size="sm"
      mt={4}
      styles={{
        root: { width: "100%" },
        input: {
          backgroundColor: isPreviewMode ? colors.warning[2] : "transparent",
        },
      }}
    />
  );

  const renderOptionRow = (
    option: string,
    isOptionSelected: boolean,
    onToggle: () => void
  ) => (
    <Box
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        width: "100%",
      }}
    >
      <Box style={{ flexShrink: 0, paddingTop: 1 }}>
        {isOptionSelected ? (
          <Box
            component="img"
            src={CheckedIcon}
            height={20}
            width={20}
            style={{
              cursor: isPreviewMode ? "not-allowed" : "pointer",
            }}
            onClick={() => !isPreviewMode && onToggle()}
          />
        ) : (
          <Checkbox
            disabled={
              isPreviewMode || selected?.includes("Unable to determine")
            }
            variant="outline"
            checked={false}
            onChange={(event) =>
              handleChange(option, event.currentTarget.checked)
            }
            styles={{
              input: {
                cursor:
                  isPreviewMode || selected?.includes("Unable to determine")
                    ? "not-allowed"
                    : "pointer",
              },
              root: { alignItems: "center" },
            }}
          />
        )}
      </Box>

      <Text
        size="12px"
        lh="20px"
        c={colors.gray[8]}
        fw={400}
        style={{
          flex: 1,
          minWidth: 0,
          wordBreak: "break-word",
          fontFamily: "Roboto, sans-serif",
          backgroundColor:
            isPreviewMode && isOptionSelected
              ? colors.warning[2]
              : "transparent",
          padding: "2px",
          borderRadius: "2px",
          cursor: isPreviewMode ? "default" : "pointer",
        }}
        onClick={() => !isPreviewMode && onToggle()}
      >
        {option}
      </Text>
    </Box>
  );

  return (
    <Paper
      px='md'
      py={"xs"}
      withBorder
      radius='md'
      className={className}
      pb={hasVisibleSpecifyInput || (otherOption && selected.includes(otherOptionLabel)) ? "sm" : "xs"}
      style={{
        borderColor: colors.primary[3],
        borderWidth: "1px",
        minWidth: 275,
        width: "100%",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      <Box mb='4px'>
        <Text
          size='14px'
          lh='24px'
          c={colors.gray[8]}
          fw={500}
          style={{
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {title}
          {required && <span style={{ color: colors.danger[4] }}> *</span>}
        </Text>
      </Box>

      <Stack gap={"4px"}>
        {options?.map((option) => {
          const isOptionSelected = selected.includes(option);

          return (
            <Box key={option} w="100%">
              {renderOptionRow(option, isOptionSelected, () =>
                handleChange(option, !isOptionSelected)
              )}

              {showSpecifyInput(option) && (
                <Box pl={28} pr={4} w="100%" style={{ boxSizing: "border-box" }}>
                  {renderSpecifyInput()}
                </Box>
              )}
            </Box>
          );
        })}

        {otherOption && otherOptionLabel && (
          <Box w="100%">
            {(() => {
              const isOtherSelected = selected.includes(otherOptionLabel);

              return (
                <Stack gap="4px">
                  {renderOptionRow(otherOptionLabel, isOtherSelected, () =>
                    handleChange(otherOptionLabel, !isOtherSelected)
                  )}

                  {isOtherSelected && (
                    <Box pl={28} pr={4} w="100%" style={{ boxSizing: "border-box" }}>
                      {renderSpecifyInput()}
                    </Box>
                  )}
                </Stack>
              );
            })()}
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// RadioGroup Component
interface RadioGroupProps extends FormGroupBaseProps {
  options: string[];
  onChange?: (selectedOption: string[]) => void;
  selectedOption?: string[];
  otherOption?: boolean;
  otherText?: string;
  onOtherChange?: (text: string) => void;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  title,
  options,
  required = false,
  onChange,
  selectedOption = [],
  className,
  isPreviewMode = false, // Default to false
  otherOption = false,
  otherText = "",
  onOtherChange,
}) => {
  // Use state from props if provided, otherwise use local state
  const [value, setValue] = useState<string>(selectedOption[0]);
  const [localOtherText, setLocalOtherText] = useState<string>(otherText);

  // Update local state when props change
  useEffect(() => {
    setValue(selectedOption[0] ?? "");
  }, [selectedOption]);
  useEffect(() => {
    setLocalOtherText(otherText);
  }, [otherText]);

  const handleChange = (selectedValue: string) => {
    setValue(selectedValue);
    onChange?.([selectedValue]);
  };
  const handleOtherTextChange = (value: string) => {
    setLocalOtherText(value);
    onOtherChange?.(value);
  };

  const colors = useColors();

  return (
    <Paper
      px='md'
      py={"xs"}
      withBorder
      radius='md'
      className={className}
      style={{
        borderColor: colors.primary[3],
        borderWidth: "1px",
        minHeight: 120,
        minWidth: 275,
      }}
    >
      <Box mb='4px'>
        <Text
          size='14px'
          lh='24px'
          c={colors.gray[8]}
          fw={500}
          style={{
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {title}
          {required && <span style={{ color: colors.danger[4] }}> *</span>}
        </Text>
      </Box>

      <Radio.Group value={value} onChange={handleChange}>
        <Stack gap={"4px"}>
          {options.map((option) => (
            <Radio
              disabled={isPreviewMode}
              key={option}
              value={option}
              classNames={{
                radio: "custom-radio-input",
                icon: "custom-radio-icon",
              }}
              label={
                <Text
                  size='12px'
                  lh='20px'
                  c={colors.gray[8]}
                  fw={400}
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    backgroundColor:
                      isPreviewMode && value === option
                        ? colors.warning[2]
                        : "transparent",
                    padding: "2px",
                    borderRadius: "2px",
                    display: "inline-block",
                  }}
                >
                  {option}
                </Text>
              }
            />
          ))}
          {otherOption && (
            <Radio
              disabled={isPreviewMode}
              value={OTHER_OPTION_VALUE}
              classNames={{
                radio: "custom-radio-input",
                icon: "custom-radio-icon",
              }}
              label={
                <Text
                  size='12px'
                  lh='20px'
                  c={colors.gray[8]}
                  fw={400}
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    backgroundColor:
                      isPreviewMode && value === OTHER_OPTION_VALUE
                        ? colors.warning[2]
                        : "transparent",
                    padding: "2px",
                    borderRadius: "2px",
                    display: "inline-block",
                  }}
                >
                  {OTHER_OPTION_VALUE}
                </Text>
              }
            />
          )}
          {value === OTHER_OPTION_VALUE && (
            <TextInput
              maxLength={100}
              placeholder='Please specify'
              value={localOtherText}
              readOnly={isPreviewMode}
              onChange={(event) =>
                handleOtherTextChange(event.currentTarget.value)
              }
              size='sm'
              styles={{
                input: {
                  backgroundColor: isPreviewMode
                    ? colors.warning[2]
                    : "transparent",
                },
              }}
            />
          )}
        </Stack>
      </Radio.Group>
    </Paper>
  );
};
